/**
 * etichettaDocumento.js — il NOME DEL DOCUMENTO davanti a un codice, nei PDF.
 *
 * Una copia sola della regola che fino al 2/11/2026 viveva solo nella stampa
 * della fattura (pdfFattura.etichettaCodice, Alex 3/10/2026, punto 8: «mai
 * "C.F." davanti a un codice che non lo e'»). Gli altri PDF - il modulo
 * anagrafico e i consensi della pratica, il referto, l'attestazione di
 * esenzione, il riquadro dei consensi dai modelli - scrivevano «C.F.» o
 * «Codice fiscale» davanti a qualunque codice: un codice STP, un passaporto,
 * il codice ISS di San Marino stampati come codice fiscale. Decisione del 3/10
 * (registro delle richieste, punto (e)), confermata da Alex il 2/11/2026
 * (riferito da Claude): «l'etichetta segue il tipo di documento».
 *
 * DECIDE IL TIPO DI DOCUMENTO di chi ha quel codice (pazienti.tipo_documento,
 * l'elenco TIPI_DOCUMENTO di utils/codiceFiscale.js), ma solo se descrive il
 * codice che si stampa: la sua forma deve essere una di quelle che il tipo
 * ammette. Altrimenti decide la forma: «C.F.» solo per la forma di un codice
 * fiscale, «Codice STP» e «Codice ENI» per la loro, e per tutto il resto
 * «Codice», un nome che non afferma niente. Un tipo sconosciuto (o assente:
 * un referto concluso prima di questa regola non lo porta) passa dalla forma.
 *
 * Chi passa il tipo deve sapere che e' il tipo DI QUEL codice: la fattura
 * passa il tipo solo se l'intestatario ha lo stesso codice della scheda
 * dell'emissione (intestatario_documento, migration 296), gli altri PDF
 * leggono codice e tipo dalla stessa riga.
 *
 * DUE COPIE, UNA PROVA (2/11/2026). Questo file vive in backend/utils (i PDF)
 * e in frontend/src/utils (le anteprime a schermo: il riquadro del referto,
 * il riepilogo della pratica, del completamento dei consensi e della firma),
 * uguale salvo le righe dell'importazione e delle esportazioni.
 * frontend/scripts/verificaEtichettaDocumento.js, nel prebuild, li carica
 * tutti e due e confronta le risposte su ogni tipo e su qualche migliaio di
 * codici: se divergono la build non parte. Chi cambia una copia cambia
 * l'altra.
 */
import { formaCodice, pulisciCodice } from './codiceFiscale';

/* L'ISS non ha una forma sua in formaCodice: sette cifre al piu'. */
function formaStampata(codice) {
    const forma = formaCodice(codice);
    return forma === 'altro' && /^\d{1,7}$/.test(pulisciCodice(codice)) ? 'iss' : forma;
}

const ETICHETTE_FORMA = { cf: 'C.F.', stp: 'Codice STP', eni: 'Codice ENI' };
const ETICHETTE_TIPO = {
    cf_italiano: { forme: ['cf'], etichetta: () => 'C.F.' },
    iss_san_marino: { forme: ['iss'], etichetta: () => 'Codice ISS San Marino' },
    estero: { forme: ['altro', 'iss'], etichetta: () => 'Documento estero' },
    stp_eni: { forme: ['stp', 'eni', 'altro'], etichetta: (forma) => ETICHETTE_FORMA[forma] || 'Codice STP o ENI' },
};

/**
 * @param {string|null} tipo   tipo di documento di chi ha il codice, o null
 * @param {string} codice      il codice che si stampa
 * @param {object} [opz]       { lunga: true } per le righe «etichetta: valore»
 *                             dei moduli: «Codice fiscale» al posto di «C.F.»
 */
function etichettaDocumento(tipo, codice, { lunga = false } = {}) {
    const forma = formaStampata(codice);
    const t = tipo ? ETICHETTE_TIPO[tipo] : null;
    const e = t && t.forme.includes(forma) ? t.etichetta(forma) : (ETICHETTE_FORMA[forma] || 'Codice');
    return lunga && e === 'C.F.' ? 'Codice fiscale' : e;
}

export { etichettaDocumento, formaStampata };
